/**
 * A task card opens its page and then jumps to the region the job produced.
 * `TaskNavigation.section` travels as this search param; the target region is
 * marked with `data-task-section="<key>"` (keys from `TASK_NAVIGATION_SECTION`).
 * When the job also produced a specific item, `TaskNavigation.itemIds` travel as
 * `?highlight=` and the item is marked with `data-task-item="<id>"`.
 *
 * Ownership (one scroller each — never race):
 * - `?variant=sketch|panels` → ScriptStoryboardView scrolls `[data-task-storyboard]`
 * - `?scriptId=` / `?highlight=` / `?opportunityId=` + `?section=` → ScriptsTab scrolls `[data-task-item]`
 * - `?section=` alone → useTaskSectionJump scrolls `[data-task-scroller]` to top
 *
 * Mark `data-task-section` only on the tab that owns it. A wrapper that stays
 * mounted on Opportunities would make a scripts jump "succeed" while the row
 * is still unmounted (search/filter), then clear `?section=` before the row
 * can appear.
 *
 * Clearing `?section=` (and `?variant=` for storyboard) after a jump is what
 * makes a later identical card click re-trigger navigation.
 */
export const TASK_SECTION_SEARCH_PARAM = "section";

/** Comma-joined item ids from `TaskNavigation.itemIds` — first id is the scroll target. */
export const TASK_ITEM_SEARCH_PARAM = "highlight";

/** Deep-link expand/scroll id used by script storyboard and hook-variation cards. */
const SCRIPT_ID_SEARCH_PARAM = "scriptId";
const BRIEF_ID_SEARCH_PARAM = "briefId";

/** Angle group to expand when a generate-scripts card has no scriptId yet. */
const OPPORTUNITY_ID_SEARCH_PARAM = "opportunityId";

/** Sketch / Animatic deep-link seed — owned by ScriptStoryboardView. */
const VARIANT_SEARCH_PARAM = "variant";

/** Attribute on the in-card Storyboard section for Sketch / Animatic deep links. */
export const TASK_STORYBOARD_ATTR = "data-task-storyboard";

/** Attribute on the nested results scroller for section-only jumps (e.g. "N scripts"). */
export const TASK_SCROLLER_ATTR = "data-task-scroller";

/** The region often mounts only after its data resolves, so the caller keeps retrying. */
const SECTION_WAIT_MS = 5_000;

/** Offset matching `scroll-mt-6` so the row is not flush against the scroller edge. */
const SCROLL_TOP_OFFSET_PX = 24;

export interface ScrollToTaskTargetOptions {
  allowSectionFallback?: boolean;
}

function escapeAttr(value: string): string {
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(value) : value;
}

/**
 * The scripts list lives in a nested `overflow-y-auto` panel; scrolling the
 * window is a no-op because the page shell itself does not scroll. Drive the
 * nearest scrollable ancestor when one exists.
 */
export function findScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Collapsed script bodies stay mounted inside `[inert]` (`CollapsibleReveal`).
 * Treating those as found would "succeed" the jump and clear the URL before the
 * row actually opens — visually a no-op.
 */
export function isVisibleTaskTarget(element: HTMLElement): boolean {
  return !element.closest("[inert]");
}

/** Scroll an element into view inside its nearest overflow scroller when possible. */
export function scrollElementIntoTaskView(element: HTMLElement): void {
  const scroller = findScrollParent(element);
  if (scroller) {
    const elementTop = element.getBoundingClientRect().top;
    const scrollerTop = scroller.getBoundingClientRect().top;
    const top = elementTop - scrollerTop + scroller.scrollTop - SCROLL_TOP_OFFSET_PX;
    scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return;
  }
  element.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function readTaskSection(search: unknown): string | null {
  if (typeof search !== "object" || search === null) return null;
  const value = (search as Record<string, unknown>)[TASK_SECTION_SEARCH_PARAM];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Prefer `?highlight=` (from `itemIds`); fall back to `?scriptId=` then `?briefId=`
 * so storyboard / brief AI cards still expand when an older task record never set `itemIds`.
 */
export function readTaskItem(search: unknown): string | null {
  if (typeof search !== "object" || search === null) return null;
  const record = search as Record<string, unknown>;

  const highlight = record[TASK_ITEM_SEARCH_PARAM];
  if (typeof highlight === "string" && highlight.length > 0) {
    const first = highlight.split(",")[0]?.trim();
    if (first && first.length > 0) return first;
  }

  const scriptId = record[SCRIPT_ID_SEARCH_PARAM];
  if (typeof scriptId === "string" && scriptId.length > 0) return scriptId;

  const briefId = record[BRIEF_ID_SEARCH_PARAM];
  return typeof briefId === "string" && briefId.length > 0 ? briefId : null;
}

/** Angle group a generate-scripts card asked to expand when no scriptId exists. */
export function readTaskOpportunityId(search: unknown): string | null {
  if (typeof search !== "object" || search === null) return null;
  const value = (search as Record<string, unknown>)[OPPORTUNITY_ID_SEARCH_PARAM];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** True when Sketch/Animatic deep-link — scroll is owned by ScriptStoryboardView. */
export function readTaskPreferStoryboard(search: unknown): boolean {
  if (typeof search !== "object" || search === null) return false;
  const variant = (search as Record<string, unknown>)[VARIANT_SEARCH_PARAM];
  return variant === "sketch" || variant === "panels";
}

/** False when the section is not on the page (yet) — nothing was scrolled. */
export function scrollToTaskSection(section: string): boolean {
  const element = document.querySelector<HTMLElement>(
    `[data-task-section="${escapeAttr(section)}"]`,
  );
  if (!element) return false;

  // Prefer an explicit results scroller so section-only jumps (e.g. "N scripts")
  // do not no-op on an overflow-hidden shell.
  const marked = element.querySelector<HTMLElement>(`[${TASK_SCROLLER_ATTR}]`);
  if (marked) {
    marked.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }

  const nested = findNestedScroller(element);
  if (nested) {
    nested.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }

  const { overflowY } = window.getComputedStyle(element);
  if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
    element.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }

  scrollElementIntoTaskView(element);
  return true;
}

function findNestedScroller(element: HTMLElement): HTMLElement | null {
  const candidates = element.querySelectorAll<HTMLElement>("*");
  for (const candidate of candidates) {
    const { overflowY } = window.getComputedStyle(candidate);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return candidate;
    }
  }
  return null;
}

/**
 * Prefer a precise `data-task-item` row when an item id is known. While an item
 * is expected, do not fall back to the section — the row often mounts after its
 * data resolves. `allowSectionFallback` is for the final attempt after the wait
 * window so a miss still lands somewhere useful.
 */
export function scrollToTaskTarget(
  section: string,
  item?: string | null,
  options?: ScrollToTaskTargetOptions,
): boolean {
  if (item) {
    const itemElement = document.querySelector<HTMLElement>(
      `[data-task-item="${escapeAttr(item)}"]`,
    );
    if (itemElement && isVisibleTaskTarget(itemElement)) {
      scrollElementIntoTaskView(itemElement);
      return true;
    }
    if (!options?.allowSectionFallback) return false;
  }

  return scrollToTaskSection(section);
}

export interface TaskSectionJumpHandle {
  cancel: () => void;
}

/**
 * Retry `scrollToTaskTarget` across animation frames until the region (or item)
 * mounts or the wait window elapses. Always calls `onSettled` — on success and
 * on timeout — so the caller can drop search params and a miss cannot brick
 * the next click (identical URL = no-op navigation).
 */
export function jumpToTaskSectionWhenReady(
  section: string,
  onSettled: () => void,
  now: () => number = Date.now,
  item?: string | null,
): TaskSectionJumpHandle {
  const deadline = now() + SECTION_WAIT_MS;
  let frame = 0;
  let cancelled = false;

  const attempt = () => {
    if (cancelled) return;
    if (scrollToTaskTarget(section, item)) {
      onSettled();
      return;
    }
    if (now() >= deadline) {
      // Best-effort land on section/row, then always settle so params clear.
      if (item) {
        scrollToTaskTarget(section, item, { allowSectionFallback: true });
      } else {
        scrollToTaskSection(section);
      }
      onSettled();
      return;
    }
    frame = window.requestAnimationFrame(attempt);
  };

  frame = window.requestAnimationFrame(attempt);

  return {
    cancel: () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    },
  };
}

export interface StoryboardJumpHandle {
  cancel: () => void;
}

/**
 * Wait until `element` is visible (not inside `[inert]`), scroll it into the
 * nested results scroller, then settle. Always settles on timeout so a miss
 * cannot leave `?variant=` stuck in the URL.
 */
export function jumpToTaskTargetWhenReady(
  getElement: () => HTMLElement | null,
  onSettled: () => void,
  now: () => number = Date.now,
): StoryboardJumpHandle {
  const deadline = now() + SECTION_WAIT_MS;
  let frame = 0;
  let cancelled = false;

  const attempt = () => {
    if (cancelled) return;
    const element = getElement();
    if (element && isVisibleTaskTarget(element)) {
      scrollElementIntoTaskView(element);
      onSettled();
      return;
    }
    if (now() >= deadline) {
      if (element) scrollElementIntoTaskView(element);
      onSettled();
      return;
    }
    frame = window.requestAnimationFrame(attempt);
  };

  frame = window.requestAnimationFrame(attempt);

  return {
    cancel: () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    },
  };
}
